import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence, Variants } from 'framer-motion';

const Text = styled(motion.h2)`
  position: absolute;
  padding: 0 2px;
  color: var(--color-white);
  pointer-events: none;
  font-size: var(--font-size-body-large-01);
  font-weight: var(--font-weight-normal);
  line-height: var(--line-height-md);
  font-family: 'Epilogue';
  padding: 40px;
  font-weight: var(--weight);

  @media ${(p) => p.theme.queries.tabletAndDown} {
    padding: 30px;
  }
`;

const AspectRatioBox = styled.div<{ perRow: number }>`
  width: 100%;
  padding-top: 100%;
  position: relative;
  padding-top: 290px;

  ${({ perRow }) => {
    if (perRow === 4) {
      return `
        flex: 0 0 25%;
        max-width: 25%;
        padding-top: 25%;
      `;
    }

    if (perRow === 3) {
      return `
        flex: 0 0 33.33333%;
        max-width: 33.33333%;
        padding-top: 290px ;
      `;
    }
  }}

  @media ${(p) => p.theme.queries.tabletAndDown} {
    padding-top: 200px;
  }

  @media ${(p) => p.theme.queries.phoneAndDown} {
    flex: 0 0 50%;
    max-width: 50%;
    padding-top: 50%;
  }
`;

const AspectRatioBoxInside = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 2.5%;

  @media (min-width: 576px) {
    padding: 2.5%;
  }

  @media (min-width: 768px) {
    padding: 5%;
  }

  @media (min-width: 992px) {
    padding: 7%;
  }
`;

const ContentBox = styled.div`
  position: relative;
  height: 100%;
  border-radius: 20px;
  background: var(--bg-color, #0e0e0e);
  transition: 1s cubic-bezier(0.44, 0.21, 0, 1);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &:hover {
    background: var(--bg-hover-color, linear-gradient(#213672, #2245a0));
  }
`;

interface Props {
  label: string;
  value: string;
  bgColor?: string;
  bgHover?: string;
  perRow: number;
}

const FeatureSectionItem: React.FC<Props> = ({
  label,
  value,
  bgColor,
  bgHover,
  perRow,
}) => {
  const [text, setText] = useState<string>(label);

  const variants: Variants = {
    initial: {
      y: 15,
      opacity: 0,
      scale: 0.5,
    },
    animate: { y: 0, opacity: 1 },
    exit: { y: -10, opacity: 0, scale: 0.5 },
  };

  const fontWeight = text === label ? '500' : '300';

  return (
    <AspectRatioBox perRow={perRow}>
      <AspectRatioBoxInside
        onMouseEnter={() => setText(value)}
        onMouseLeave={() => setText(label)}
      >
        <ContentBox
          style={{ '--bg-color': bgColor, '--bg-hover-color': bgHover }}
        >
          <AnimatePresence initial={false}>
            <Text
              key={text}
              style={{ '--weight': fontWeight }}
              variants={variants}
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -10, opacity: 0, scale: 0.5 }}
              transition={{ duration: 0.5 }}
            >
              {text}
            </Text>
          </AnimatePresence>
        </ContentBox>
      </AspectRatioBoxInside>
    </AspectRatioBox>
  );
};

export default FeatureSectionItem;
